import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/Shared";
import { TableSkeleton } from "../components/Skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { MessageSquare, CheckCircle2, XCircle } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "sent", label: "Sent" },
  { key: "failed", label: "Failed" },
];

export default function SmsLogs() {
  const [status, setStatus] = useState("all");
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["sms-logs", status],
    queryFn: () => api.get("/sms-logs", { params: { status } }).then((r) => r.data),
  });

  return (
    <div className="space-y-6" data-testid="sms-logs-page">
      <PageHeader title="SMS Delivery Logs" subtitle="Every 2FA / alert text Twilio attempted, with its delivery status." />

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`sms-log-filter-${f.key}`}
            onClick={() => setStatus(f.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${status === f.key ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <TableSkeleton cols={5} />
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="sms-logs-empty">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            No SMS activity yet. Codes are texted here once a user has a 2FA phone and Twilio is approved.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Purpose</TableHead>
                <TableHead>Sender</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.id || i} data-testid="sms-log-row">
                  <TableCell className="whitespace-nowrap text-sm text-slate-500">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell className="font-mono-num text-sm">{r.to}</TableCell>
                  <TableCell className="text-sm">{r.context || "general"}</TableCell>
                  <TableCell className="text-sm text-slate-500">{r.sender || "—"}</TableCell>
                  <TableCell>
                    {r.sent ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Sent{r.status ? ` · ${r.status}` : ""}
                      </span>
                    ) : (
                      <span title={r.reason || ""} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-red-50 text-red-700">
                        <XCircle className="h-3.5 w-3.5" /> Failed
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {rows.some((r) => !r.sent && r.reason) && (
        <p className="text-xs text-slate-400">Tip: hover a “Failed” badge to see Twilio’s reason.</p>
      )}
    </div>
  );
}
